import { useQuery } from "@tanstack/react-query";
import { ShieldAlert, Wallet, Crosshair, Users, Database, Eye } from "lucide-react";
import { fetchConfig } from "@/lib/api";
import SectionHeader from "@/components/SectionHeader";

const BLOCKS = [
  {
    icon: Crosshair,
    title: "What Money Printer Is",
    body: "Money Printer is a transparency-first NBA analytics and picks membership built for the 2026–27 season. We publish researched selections — game picks, player props, moneylines, spreads and totals — with the reasoning attached. We don't promise guaranteed wins; we show our process.",
    testid: "hiw-what",
  },
  {
    icon: Database,
    title: "How Picks Are Selected",
    body: "Every selection starts with matchup data: recent form, usage rate, minutes projections, rest differentials and opponent scheme tendencies. A pick only makes the card when our projection shows a measurable edge against the market number. NBA data is powered by a professional feed (BALLDONTLIE) so the numbers behind each writeup are consistent and verifiable.",
    testid: "hiw-selection",
  },
  {
    icon: Wallet,
    title: "How Units Work",
    body: "A unit is a standard amount used to track betting performance. Money Printer uses 1U = $100 as its reference, but your unit can be any size — if your standard stake is $10, then 1U = $10 for you. Every pick is graded in units, not dollars, so results are comparable regardless of stake size. A +12.4U season means twelve standard bets of profit, tracked pick by pick on the public ledger.",
    testid: "hiw-units",
  },
  {
    icon: Eye,
    title: "How Results Are Tracked",
    body: "Every official pick is recorded the moment it's posted and settled publicly. Wins, losses and pushes all stay on the ledger permanently — nothing is deleted or quietly edited. The record, ROI and cumulative units chart on the Results page are computed from that same ledger.",
    testid: "hiw-tracking",
  },
  {
    icon: Users,
    title: "Membership & Community",
    body: "Handled securely by Whop in the background while keeping the experience fully branded. After joining, members receive Discord access with the correct role automatically. Discord hosts the community and pick alerts today; over time, member content moves into your Money Printer dashboard on this site.",
    testid: "hiw-membership",
  },
];

export default function HowItWorks() {
  const { data: config } = useQuery({ queryKey: ["config"], queryFn: fetchConfig, staleTime: 300_000 });
  const vipUrl = config?.whop?.vip_checkout_url || "/#pricing";

  return (
    <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-20 lg:px-8" data-testid="how-it-works-page">
      <SectionHeader
        eyebrow="The Full Picture"
        title="How It Works"
        sub="What you're actually buying: a research process, a transparent record, and a community — explained without the usual picks-industry fog."
        testid="hiw-header"
      />

      <div className="grid gap-6 md:grid-cols-2">
        {BLOCKS.map((b) => (
          <article key={b.title} className="card-mp card-hover p-6 sm:p-7" data-testid={b.testid}>
            <div className="flex h-11 w-11 items-center justify-center rounded-lg border border-[#00E676]/30 bg-[#00E676]/10">
              <b.icon size={20} className="text-[#00E676]" />
            </div>
            <h3 className="mt-5 font-display text-xl font-bold uppercase tracking-wide text-white">{b.title}</h3>
            <p className="mt-2.5 text-sm leading-relaxed text-slate-400">{b.body}</p>
          </article>
        ))}

        <article className="card-mp border-[#F59E0B]/30 p-6 sm:p-7" data-testid="hiw-bankroll">
          <div className="flex h-11 w-11 items-center justify-center rounded-lg border border-[#F59E0B]/30 bg-[#F59E0B]/10">
            <Wallet size={20} className="text-[#F59E0B]" />
          </div>
          <h3 className="mt-5 font-display text-xl font-bold uppercase tracking-wide text-white">Bankroll Education</h3>
          <ul className="mt-3 space-y-2.5 text-sm leading-relaxed text-slate-400">
            <li>· <span className="text-slate-200">Understand Units:</span> Money Printer uses a standard reference of 1U = $100 for tracking our results. Your personal unit value can be different.</li>
            <li>· <span className="text-slate-200">Set Your Own Stake:</span> If your standard stake is $10, then 1U = $10. You don't need to match our $100 reference.</li>
            <li>· <span className="text-slate-200">Protect Your Bankroll:</span> Choose an affordable unit size, typically no more than 1–2% of your total bankroll.</li>
            <li>· <span className="text-slate-200">Never Chase Losses:</span> Stick to your predetermined unit size regardless of recent results.</li>
            <li>· <span className="text-slate-200">Track Performance:</span> Our results are measured in units so everyone can compare performance regardless of their individual stake size.</li>
            <li>· <span className="text-slate-200">No Guarantees:</span> Every pick carries risk. Past performance does not guarantee future returns.</li>
          </ul>
        </article>

        <article className="card-mp border-[#EF4444]/25 p-6 sm:p-7" data-testid="hiw-responsible">
          <div className="flex h-11 w-11 items-center justify-center rounded-lg border border-[#EF4444]/30 bg-[#EF4444]/10">
            <ShieldAlert size={20} className="text-[#EF4444]" />
          </div>
          <h3 className="mt-5 font-display text-xl font-bold uppercase tracking-wide text-white">Responsible Gambling</h3>
          <p className="mt-2.5 text-sm leading-relaxed text-slate-400">
            Money Printer is 18+ and for entertainment and information. No pick is ever guaranteed, and past results
            never promise future performance. If betting stops being fun, stop. Free, confidential help is available 24/7 in Australia at
            <a href="tel:1800858858" className="font-semibold text-slate-200 underline-offset-2 hover:underline"> Gambling Help Online 1800 858 858</a> (gamblinghelponline.org.au). Self-exclusion: <a href="https://www.betstop.gov.au" target="_blank" rel="noopener noreferrer" className="font-semibold text-slate-200 underline-offset-2 hover:underline">BetStop</a>.
          </p>
          <a href={vipUrl} target={vipUrl.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" data-testid="hiw-join-btn" className="btn-primary mt-6">
            Join Money Printer
          </a>
        </article>
      </div>
    </div>
  );
}
